import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CircleStackIcon,
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { DataTable, ColumnDef } from '../../components/ui/DataTable';
import { PageLoading } from '../../components/ui/LoadingSpinner';
import { BackupService, BackupInfo } from '../../services/backup.service';
import toast from 'react-hot-toast';
import { formatDistanceToNow, format } from 'date-fns';

interface BackupInfoWithActions extends BackupInfo {
  actions?: string;
}

const BackupManager: React.FC = () => {
  const [selectedBackup, setSelectedBackup] = useState<string | null>(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const queryClient = useQueryClient();

  const { data: backupData, isLoading, error } = useQuery({
    queryKey: ['backups'],
    queryFn: BackupService.listBackups,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: connectionTest } = useQuery({
    queryKey: ['backup-connection'],
    queryFn: BackupService.testConnection,
    refetchInterval: 60000, // Test connection every minute
  });

  const createBackupMutation = useMutation({
    mutationFn: BackupService.createBackup,
    onSuccess: () => {
      toast.success('Backup created successfully');
      queryClient.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (error: any) => {
      toast.error(`Backup failed: ${error.response?.data?.message || error.message}`);
    },
  });

  const restoreBackupMutation = useMutation({
    mutationFn: (filename: string) => BackupService.restoreBackup(filename),
    onSuccess: () => {
      toast.success('Database restored successfully');
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setShowRestoreConfirm(false);
      setSelectedBackup(null);
    },
    onError: (error: any) => {
      toast.error(`Restore failed: ${error.response?.data?.message || error.message}`);
    },
  });

  const deleteBackupMutation = useMutation({
    mutationFn: (filename: string) => BackupService.deleteBackup(filename),
    onSuccess: () => {
      toast.success('Backup deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setShowDeleteConfirm(false);
      setSelectedBackup(null);
    },
    onError: (error: any) => {
      toast.error(`Delete failed: ${error.response?.data?.message || error.message}`);
    },
  });

  const formatBytes = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'destructive';
      case 'in_progress': return 'warning';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'manual': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const backupColumns: ColumnDef<BackupInfoWithActions>[] = [
    {
      key: 'filename',
      title: 'Filename',
      render: (filename: string) => (
        <div className="font-mono text-sm">{filename}</div>
      ),
    },
    {
      key: 'type',
      title: 'Type',
      render: (type: string) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(type)}`}>
          {type === 'scheduled' ? 'Scheduled' : 'Manual'}
        </span>
      ),
    },
    {
      key: 'size',
      title: 'Size',
      render: (size: number) => formatBytes(size),
    },
    {
      key: 'createdAt',
      title: 'Created',
      render: (createdAt: string) => (
        <div>
          <div className="text-sm text-gray-900">{format(new Date(createdAt), 'MMM d, yyyy')}</div>
          <div className="text-xs text-gray-500">{formatDistanceToNow(new Date(createdAt), { addSuffix: true })}</div>
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (status: string) => (
        <Badge variant={getStatusColor(status)} size="sm">
          {status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_: any, backup: BackupInfoWithActions) => (
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedBackup(backup.filename);
              setShowRestoreConfirm(true);
            }}
            disabled={backup.status !== 'completed'}
          >
            <CloudArrowDownIcon className="h-4 w-4 mr-1" />
            Restore
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
            onClick={() => {
              setSelectedBackup(backup.filename);
              setShowDeleteConfirm(true);
            }}
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <PageLoading message="Loading backup manager..." />;
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent>
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading backups</h3>
              <p className="mt-1 text-sm text-gray-500">
                {error instanceof Error ? error.message : 'An unexpected error occurred'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { backups = [], stats } = backupData || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <CircleStackIcon className="h-8 w-8 text-gray-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Backup Manager</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage database backups and recovery
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Connection status */}
          <div className="flex items-center gap-2">
            {connectionTest?.connected ? (
              <>
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                <span className="text-sm text-green-600">Database Connected</span>
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                <span className="text-sm text-red-600">Database Disconnected</span>
              </>
            )}
          </div>

          <Button
            onClick={() => createBackupMutation.mutate()}
            disabled={createBackupMutation.isPending}
          >
            {createBackupMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                Create Backup
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent>
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-blue-100">
                  <CircleStackIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-600">Total Backups</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalBackups}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-green-100">
                  <ServerIcon className="h-5 w-5 text-green-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-600">Total Size</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatBytes(stats.totalSize)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-purple-100">
                  <ClockIcon className="h-5 w-5 text-purple-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-600">Scheduled</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.scheduledBackups}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-orange-100">
                  <CloudArrowUpIcon className="h-5 w-5 text-orange-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-600">Manual</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.manualBackups}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Backups Table */}
      <Card>
        <CardHeader>
          <CardTitle>Available Backups</CardTitle>
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <div className="text-center py-12">
              <CircleStackIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No backups found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Create your first backup to get started.
              </p>
            </div>
          ) : (
            <DataTable
              data={backups}
              columns={backupColumns}
              searchable={true}
              filterable={true}
              sortable={true}
            />
          )}
        </CardContent>
      </Card>

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && selectedBackup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Confirm Database Restore</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              This will replace all current data with the backup from{' '}
              <strong className="font-mono">{selectedBackup}</strong>. This action cannot be undone.
              A backup of the current database will be created before the restore.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRestoreConfirm(false);
                  setSelectedBackup(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => restoreBackupMutation.mutate(selectedBackup)}
                disabled={restoreBackupMutation.isPending}
              >
                {restoreBackupMutation.isPending ? 'Restoring...' : 'Restore'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedBackup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <TrashIcon className="h-6 w-6 text-red-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Delete Backup</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete the backup{' '}
              <strong className="font-mono">{selectedBackup}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedBackup(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteBackupMutation.mutate(selectedBackup)}
                disabled={deleteBackupMutation.isPending}
              >
                {deleteBackupMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupManager;